/**
 * F16.H: data hooks for the portal SharePoint Explorer. Live-only:
 * per-portal MSAL token + lockedSiteIds filter, all reads wrapped in
 * `withRetry(READ_POLICY)` inside `lib/graph/sharepoint`.
 */

import { useEffect, useState } from 'react';
import type { Site, Drive } from '@microsoft/microsoft-graph-types';
import { useOptionalTenantBranding } from './TenantBrandingContext';
import { useOptionalPortalMsal } from './MsalInstanceProvider';
import { listSites, listDrives, getSiteById } from '../graph/sharepoint';

/**
 * Graph SharePoint site IDs use a `${hostname},${siteGuid},${webGuid}`
 * triple. The middle GUID is the stable identity — `hostname` casing
 * varies and Microsoft sometimes returns the triple with subtle
 * differences across endpoints. Extract the middle GUID for safe
 * cross-call comparison.
 */
function siteGuidPart(siteId: string | null | undefined): string | null {
  if (!siteId) {return null;}
  const parts = siteId.split(',');
  if (parts.length < 2) {return null;}
  return (parts[1] ?? '').trim().toLowerCase();
}

function _siteIdMatches(actual: string | null | undefined, locked: readonly string[]): boolean {
  const a = siteGuidPart(actual);
  if (!a) {return false;}
  for (const l of locked) {
    if (siteGuidPart(l) === a) {return true;}
  }
  return false;
}
// Exported guard for callers that want robust GUID-based matching once
// the SitePicker (B.3.b) lands. Kept side-by-side with the direct
// /sites/{id} lookup we use in usePortalSites today.
export { _siteIdMatches as siteIdMatchesByGuid };

export type LoadState<T> =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ok'; value: T }
  | { kind: 'error'; message: string };

// Stable empty fallback for `lockedSiteIds` when no TenantBranding is in
// the tree. Defined at module scope so its reference identity is constant
// across renders — without this, the `useEffect` dep array in usePortalSites
// would see a new `[]` each render and trigger an infinite re-render loop.
const EMPTY_LOCKED_SITE_IDS: readonly string[] = Object.freeze([]);

/**
 * Sites the portal user can see. Filtered by lockedSiteIds when set.
 *
 * Branding is optional: this hook is reachable from `<SharePointFolderPicker>`
 * which is mounted by `<Win95Desktop>` at the desktop root (outside the
 * `/c/${slug}` route subtree where `TenantBrandingProvider` mounts). When no
 * branding is present, treat `lockedSiteIds` as `[]` — the same "no portal
 * lock" path the function already takes for portals with an empty list.
 */
export function usePortalSites(): LoadState<Site[]> {
  const ctx = useOptionalTenantBranding();
  const lockedSiteIds = ctx?.branding.lockedSiteIds ?? EMPTY_LOCKED_SITE_IDS;
  const msal = useOptionalPortalMsal();
  const [state, setState] = useState<LoadState<Site[]>>({ kind: 'idle' });

  useEffect(() => {
    if (!msal || !msal.account) {
      setState({ kind: 'idle' });
      return;
    }
    let cancelled = false;
    setState({ kind: 'loading' });
    // When the portal has locked sites, look each up by id directly via
    // /sites/{id} — sidesteps SharePoint Search lag for newly-created sites.
    if (lockedSiteIds.length > 0) {
      Promise.all(lockedSiteIds.map((id) => getSiteById(id)))
        .then((results) => {
          if (cancelled) {return;}
          const ok = results.filter((s): s is NonNullable<typeof s> => s !== null);
          setState({ kind: 'ok', value: ok });
        })
        .catch((err: unknown) => {
          if (cancelled) {return;}
          setState({
            kind: 'error',
            message: (err as Error).message ?? 'Failed to load firm-locked sites.',
          });
        });
      return () => {
        cancelled = true;
      };
    }
    listSites('sharepoint')
      .then((sites) => {
        if (cancelled) {return;}
        setState({ kind: 'ok', value: sites });
      })
      .catch((err: unknown) => {
        if (cancelled) {return;}
        setState({
          kind: 'error',
          message: (err as Error).message ?? 'Failed to list SharePoint sites.',
        });
      });
    return () => {
      cancelled = true;
    };
  }, [msal, lockedSiteIds]);

  return state;
}

/** Drives (document libraries) under one site. */
export function usePortalDrives(siteId: string | null): LoadState<Drive[]> {
  const msal = useOptionalPortalMsal();
  const [state, setState] = useState<LoadState<Drive[]>>({ kind: 'idle' });

  useEffect(() => {
    if (siteId === null) {
      setState({ kind: 'idle' });
      return;
    }
    if (!msal || !msal.account) {
      setState({ kind: 'idle' });
      return;
    }
    let cancelled = false;
    setState({ kind: 'loading' });
    listDrives(siteId)
      .then((drives) => {
        if (cancelled) {return;}
        setState({ kind: 'ok', value: drives });
      })
      .catch((err: unknown) => {
        if (cancelled) {return;}
        setState({
          kind: 'error',
          message: (err as Error).message ?? 'Failed to list drives.',
        });
      });
    return () => {
      cancelled = true;
    };
  }, [siteId, msal]);

  return state;
}

